import { formatDateString, IOrganisation, IRegistration, ISupportPeriod } from '../../shared';
import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { add, isAfter, isBefore, isSameWeek, set } from 'date-fns';
import { ISupportCaseInfo } from '../models';
import { Appointment } from '../../shared/enums/appointment.enum';

interface status {
    supportType?: string;
    value: string;
    status: 'success' | 'warning' | 'error';
}

export function useWarningPanelPostalCode(
    t: TFunction,
    supportCase: ISupportCaseInfo,
    organisation?: IOrganisation,
): status {
    return useMemo(() => {
        if (supportCase?.postalCode) {
            return {
                value: supportCase?.postalCode,
                status:
                    supportCase?.postalCode === organisation?.address?.postalCode ||
                    organisation?.preferredPostalCodes?.includes(supportCase?.postalCode)
                        ? 'success'
                        : 'warning',
            };
        }
        return { value: t('notFound'), status: 'warning' };
    }, [t, supportCase, organisation]);
}

export function useWarningPanelSupportPeriods(
    t: TFunction,
    supportCase: ISupportCaseInfo,
    activeSupportPeriods: ISupportPeriod[],
): status[] {
    return useMemo(() => {
        if (activeSupportPeriods.length > 0) {
            return activeSupportPeriods
                .sort((a, b) => a.endDate.localeCompare(b.endDate))
                .map((supportPeriod) => {
                    const supportPeriodAlmostOver = isBefore(
                        new Date(supportPeriod.endDate),
                        add(set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }), {
                            weeks: 6,
                            days: 1,
                        }),
                    );
                    const futureSupportPeriod = findFutureSupportPeriod(supportCase, supportPeriod);
                    const futureSupportPeriodString = futureSupportPeriod
                        ? t('futureSupportPeriod', {
                              date: formatDateString(futureSupportPeriod.startDate),
                              interpolation: { escapeValue: false },
                          })
                        : '';
                    const supportTypeNames = supportPeriod.supportTypesInfo.map(({ supportType }) => supportType?.name);

                    return {
                        supportType: supportTypeNames.join(', ') || '',
                        value: `${t('active')} ${formatDateString(supportPeriod.endDate)}${futureSupportPeriodString}`,
                        status: supportPeriodAlmostOver && !futureSupportPeriod ? 'warning' : 'success',
                    };
                });
        }
        return [{ supportType: '', value: t('notActive'), status: 'error' }];
    }, [activeSupportPeriods, t, supportCase]);
}

export function useWarningPanelLastVisits(t: TFunction, supportCase: ISupportCaseInfo): status[] {
    return useMemo(() => {
        if (supportCase.lastRegistrations.length > 0) {
            const lastRegistrationOfEachSupportType = supportCase.lastRegistrations
                .reduce((result: IRegistration[][], current: IRegistration) => {
                    const supportType =
                        result.find((group) => group.length > 0 && group[0].supportTypeId === current.supportTypeId) ||
                        [];

                    if (supportType.length === 0) result.push(supportType);
                    supportType.push(current);
                    return result;
                }, [] as IRegistration[][])
                ?.map((registrationsPerSupportType) => registrationsPerSupportType[0]);

            return lastRegistrationOfEachSupportType.map(({ dateOfRegistration, supportType }) => {
                return {
                    supportType: supportType?.name || '',
                    value: formatDateString(dateOfRegistration),
                    status: !isSameWeek(new Date(), new Date(dateOfRegistration.slice(0, 10)), { weekStartsOn: 1 })
                        ? 'success'
                        : 'error',
                };
            });
        }
        return [{ supportType: '', value: t('notFound'), status: 'error' }];
    }, [t, supportCase]);
}

export function useWarningPanelAppointments(t: TFunction, activeSupportPeriods?: ISupportPeriod[]): status[] | [] {
    return useMemo(() => {
        return (
            activeSupportPeriods
                ?.filter(({ appointment }) => !!appointment)
                ?.map(({ appointment, supportTypesInfo }) => {
                    const supportTypeNames = supportTypesInfo.map(({ supportType }) => supportType?.name);
                    return {
                        supportType: supportTypeNames.join(', ') || '',
                        value: appointment ? `${t(appointment)}` : '-',
                        status: appointment ? validateAppointment(appointment) : 'error',
                    };
                }) || []
        );
    }, [activeSupportPeriods, t]);
}

function findFutureSupportPeriod(
    supportCase: ISupportCaseInfo,
    supportPeriod?: ISupportPeriod,
): ISupportPeriod | undefined {
    return supportCase.activeSupportPeriods.find(
        ({ startDate, supportTypesInfo }) =>
            isAfter(startDate, new Date()) &&
            supportTypesInfo
                .map(({ supportTypeId }) => supportTypeId)
                .every((id) => supportPeriod?.supportTypesInfo.map(({ supportTypeId }) => supportTypeId).includes(id)),
    );
}

function validateAppointment(appointment: Appointment): 'success' | 'error' {
    const day = new Date().getUTCDay();
    const time = new Date().getHours() < 12 ? 'AM' : 'PM';

    let appointmentDay: number | null = null;
    const appointmentTime: string = appointment.endsWith('AM') ? 'AM' : 'PM';
    switch (appointment) {
        case Appointment.SUNDAY_AM:
        case Appointment.SUNDAY_PM:
            appointmentDay = 0;
            break;
        case Appointment.MONDAY_AM:
        case Appointment.MONDAY_PM:
            appointmentDay = 1;
            break;
        case Appointment.TUESDAY_AM:
        case Appointment.TUESDAY_PM:
            appointmentDay = 2;
            break;
        case Appointment.WEDNESDAY_AM:
        case Appointment.WEDNESDAY_PM:
            appointmentDay = 3;
            break;
        case Appointment.THURSDAY_AM:
        case Appointment.THURSDAY_PM:
            appointmentDay = 4;
            break;
        case Appointment.FRIDAY_AM:
        case Appointment.FRIDAY_PM:
            appointmentDay = 5;
            break;
        case Appointment.SATURDAY_AM:
        case Appointment.SATURDAY_PM:
            appointmentDay = 6;
            break;
    }

    return appointmentDay !== null && day === appointmentDay && time === appointmentTime ? 'success' : 'error';
}
